import api from "../api";
import { store } from "../redux/store";
import { setRefreshSidebar } from "../redux/sidebarSlice";
import {
  extractTokenUsage,
  getSessionLimitMessage,
  isSessionLimitError,
  isSessionLimitReached,
} from "../utils/sessionLimit";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const isChatStreamDebugEnabled = () => {
  try {
    const envFlag = String(process.env.REACT_APP_CHAT_STREAM_DEBUG || "");
    if (/^(1|true|yes)$/i.test(envFlag)) return true;
  } catch {}
  try {
    return localStorage.getItem("chat_stream_debug") === "1";
  } catch {
    return false;
  }
};

const chatStreamDebugLog = (...args) => {
  if (!isChatStreamDebugEnabled()) return;
  try {
    console.debug("[CHAT_STREAM_UI]", ...args);
  } catch {}
};

const parseSseBlock = (block) => {
  const lines = String(block || "").split(/\r?\n/);
  let event = "message";
  const dataLines = [];
  for (const line of lines) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
  }
  const dataText = dataLines.join("\n");
  let data = null;
  if (dataText) {
    try {
      data = JSON.parse(dataText);
    } catch {
      data = { raw: dataText };
    }
  }
  return { event, data };
};

const isEventStreamResponse = (resp) => {
  const contentType = resp?.headers?.get?.("content-type") || "";
  return resp?.ok && contentType.includes("text/event-stream") && Boolean(resp.body);
};

const safeDispatchRefreshSidebar = () => {
  try {
    store.dispatch(setRefreshSidebar(true));
  } catch {
    // ignore
  }
};

class ManagedStream {
  constructor({
    sessionId,
    url,
    payload,
    accessToken,
    startedAtMs = null,
    maxReconnects = 5,
  }) {
    this.sessionId = sessionId;
    this.url = url;
    this.payload = payload;
    this.accessToken = accessToken;
    this.startedAtMs = Number.isFinite(Number(startedAtMs)) ? Number(startedAtMs) : null;
    this.maxReconnects = maxReconnects;

    this.abortController = new AbortController();
    this.closed = false;

    this.chatId = null;
    this.modelMeta = {};
    this.fullText = "";
    this.firstTokenAtMs = null;
    this.ttftSeconds = null;
    this.responseTime = null;
    this.finalResult = null;
    this.status = "starting"; // starting|streaming|done|error|stopped|aborted

    this.listeners = new Map(); // event -> Set(fn)
  }

  on(event, handler) {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    const set = this.listeners.get(event);
    set.add(handler);
    return () => {
      set.delete(handler);
    };
  }

  emit(event, detail) {
    const set = this.listeners.get(event);
    if (!set) return;
    for (const fn of set) {
      try {
        fn(detail);
      } catch {
        // ignore handler errors
      }
    }
  }

  getSnapshot() {
    return {
      sessionId: this.sessionId,
      chatId: this.chatId,
      modelMeta: this.modelMeta,
      fullText: this.fullText,
      status: this.status,
      ttftSeconds: this.ttftSeconds,
      responseTime: this.responseTime,
      finalResult: this.finalResult,
    };
  }

  abort() {
    this.status = "aborted";
    this.closed = true;
    try {
      this.abortController.abort();
    } catch {
      // ignore
    }
    this.emit("aborted", this.getSnapshot());
  }

  async refreshAccessToken() {
    const refreshToken =
      localStorage.getItem("refresh_token") ||
      sessionStorage.getItem("refresh_token");

    if (!refreshToken) return null;

    try {
      const refreshRes = await api.post(`/auth/refresh`, {
        refresh_token: refreshToken,
      });

      const newAccessToken = refreshRes.data?.access_token;
      const newRefreshToken = refreshRes.data?.refresh_token;

      if (newAccessToken) {
        localStorage.setItem("access_token", newAccessToken);
        sessionStorage.setItem("access_token", newAccessToken);
        this.accessToken = newAccessToken;
      }
      if (newRefreshToken) {
        localStorage.setItem("refresh_token", newRefreshToken);
        sessionStorage.setItem("refresh_token", newRefreshToken);
      }

      return newAccessToken || null;
    } catch {
      return null;
    }
  }

  async makeFetch({ from = 0 } = {}) {
    const isMultipart =
      typeof FormData !== "undefined" && this.payload instanceof FormData;

    const headers = {
      Accept: "text/event-stream",
      Authorization: `Bearer ${this.accessToken}`,
    };

    const fetchOptions = {
      method: "POST",
      headers,
      credentials: "include",
      body: this.payload,
      signal: this.abortController.signal,
    };

    if (!isMultipart) {
      headers["Content-Type"] = "application/json";
      fetchOptions.body = JSON.stringify(this.payload || {});
    }

    const url = from > 0 ? `${this.url}?from=${from}` : this.url;
    chatStreamDebugLog("fetch_start", {
      sessionId: this.sessionId,
      from: Number(from) || 0,
      url,
    });
    let resp = await fetch(url, fetchOptions);

    if (resp?.status === 401) {
      const refreshed = await this.refreshAccessToken();
      if (refreshed) {
        headers.Authorization = `Bearer ${refreshed}`;
        resp = await fetch(url, fetchOptions);
      }
    }

    chatStreamDebugLog("fetch_resp", {
      sessionId: this.sessionId,
      status: resp?.status,
      contentType: resp?.headers?.get?.("content-type") || "",
      ok: Boolean(resp?.ok),
    });
    return resp;
  }

  async consumeResponse(resp) {
    this.status = "streaming";
    chatStreamDebugLog("consume_start", { sessionId: this.sessionId });

    const reader = resp.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";

    while (!this.closed) {
      const { value, done } = await reader.read();
      if (done) break;

      const text = decoder.decode(value, { stream: true });
      buffer += text;

      let idx = buffer.indexOf("\n\n");
      while (idx !== -1) {
        const block = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);

        if (!block.trim() || block.startsWith(":")) {
          idx = buffer.indexOf("\n\n");
          continue;
        }

        const { event, data } = parseSseBlock(block);
        if (event !== "chunk") {
          chatStreamDebugLog("event", {
            sessionId: this.sessionId,
            event,
            hasData: data != null,
          });
        }

        // Session limit enforcement (backend can emit either a dedicated "blocked"
        // event or include token_usage on any payload).
        if (event === "blocked" || isSessionLimitError(data)) {
          this.status = "stopped";
          this.closed = true;
          try {
            this.abortController.abort();
          } catch {}
          this.emit("blocked", {
            error: data?.error || "SESSION_LIMIT_REACHED",
            message: getSessionLimitMessage(data),
            token_usage: extractTokenUsage(data) || data?.token_usage || null,
            session_id: data?.session_id || this.sessionId,
          });
          safeDispatchRefreshSidebar();
          return { finished: true };
        }

        const tokenUsage = extractTokenUsage(data);
        if (event !== "done" && tokenUsage && isSessionLimitReached(tokenUsage)) {
          this.status = "stopped";
          this.closed = true;
          try {
            this.abortController.abort();
          } catch {}
          this.emit("blocked", {
            error: "SESSION_LIMIT_REACHED",
            message: getSessionLimitMessage(data),
            token_usage: tokenUsage,
            session_id: data?.session_id || this.sessionId,
          });
          safeDispatchRefreshSidebar();
          return { finished: true };
        }

        if (event === "meta") {
          this.chatId = data?.chat_id || this.chatId;
          this.modelMeta = {
            model_name: data?.model_name || this.modelMeta.model_name,
            model_generic_name:
              data?.model_generic_name || this.modelMeta.model_generic_name,
            model_type: data?.model_type || this.modelMeta.model_type,
          };
          this.emit("meta", { ...data, chat_id: this.chatId });
        } else if (event === "chunk" && data?.token) {
          if (!this.firstTokenAtMs) {
            this.firstTokenAtMs = Date.now();
            if (this.startedAtMs) {
              this.ttftSeconds = Number(
                (((this.firstTokenAtMs - this.startedAtMs) / 1000) || 0).toFixed(2),
              );
              this.emit("first_token", { ttftSeconds: this.ttftSeconds });
            }
          }
          this.fullText += String(data.token);
          if (this.fullText.length % 2000 < String(data.token).length) {
            chatStreamDebugLog("progress", {
              sessionId: this.sessionId,
              chatId: this.chatId,
              len: this.fullText.length,
            });
          }
          this.emit("chunk", {
            token: data.token,
            fullText: this.fullText,
            ttftSeconds: this.ttftSeconds,
          });
        } else if (event === "done") {
          const resultObj =
            data?.result && typeof data.result === "object" ? data.result : null;
          this.finalResult = resultObj;

          if (typeof resultObj?.response === "string") {
            this.fullText = resultObj.response;
          } else if (typeof data?.full_response === "string") {
            this.fullText = data.full_response;
          }

          const resolvedResponseTime =
            data?.response_time ?? resultObj?.response_time ?? null;
          if (resolvedResponseTime != null) this.responseTime = resolvedResponseTime;

          this.status = "done";
          chatStreamDebugLog("done", {
            sessionId: this.sessionId,
            chatId: this.chatId,
            len: this.fullText.length,
          });
          this.emit("done", {
            fullText: this.fullText,
            result: resultObj,
            response_time: this.responseTime,
            raw: data,
          });
          safeDispatchRefreshSidebar();
          return { finished: true };
        } else if (event === "error") {
          chatStreamDebugLog("error", { sessionId: this.sessionId, chatId: this.chatId });
          this.emit("error", data || { message: "Streaming error" });
        } else if (event === "stop") {
          this.status = "stopped";
          chatStreamDebugLog("stop", { sessionId: this.sessionId, chatId: this.chatId });
          this.emit("stop", data || { reason: "stopped" });
          safeDispatchRefreshSidebar();
          return { finished: true };
        }

        idx = buffer.indexOf("\n\n");
      }
    }

    return { finished: false };
  }

  async run(initialResp = null) {
    let reconnectAttempt = 0;

    while (!this.closed) {
      let resp = initialResp;
      initialResp = null;

      if (!resp) {
        try {
          chatStreamDebugLog("reconnect_attempt", {
            sessionId: this.sessionId,
            attempt: reconnectAttempt + 1,
            from: this.fullText.length,
          });
          resp = await this.makeFetch({ from: this.fullText.length });
        } catch (err) {
          if (this.closed) return;
          reconnectAttempt += 1;
          if (reconnectAttempt > this.maxReconnects) {
            this.status = "error";
            chatStreamDebugLog("reconnect_giveup", {
              sessionId: this.sessionId,
              message: err?.message || "Network error",
            });
            this.emit("error", { message: err?.message || "Network error" });
            return;
          }
          await sleep(Math.min(1000 * 2 ** (reconnectAttempt - 1), 8000));
          continue;
        }
      }

      if (!isEventStreamResponse(resp)) {
        this.status = "error";
        chatStreamDebugLog("non_stream_response", {
          sessionId: this.sessionId,
          status: resp?.status,
          contentType: resp?.headers?.get?.("content-type") || "",
        });
        this.emit("non_stream", { resp });
        return;
      }

      reconnectAttempt = 0;

      try {
        const { finished } = await this.consumeResponse(resp);
        if (finished) return;
      } catch (err) {
        if (this.closed) return;
        reconnectAttempt += 1;
        if (reconnectAttempt > this.maxReconnects) {
          this.status = "error";
          this.emit("error", { message: err?.message || "Stream interrupted" });
          safeDispatchRefreshSidebar();
          return;
        }
        await sleep(Math.min(1000 * 2 ** (reconnectAttempt - 1), 8000));
        continue;
      }

      // connection ended without done -> reconnect
      if (!this.closed && this.status === "streaming") {
        reconnectAttempt += 1;
        if (reconnectAttempt > this.maxReconnects) {
          this.status = "error";
          this.emit("error", { message: "Connection closed" });
          safeDispatchRefreshSidebar();
          return;
        }
        await sleep(Math.min(1000 * 2 ** (reconnectAttempt - 1), 8000));
      }
    }
  }
}

export class ChatStreamManager {
  constructor() {
    this.bySession = new Map(); // sessionId -> ManagedStream
  }

  getSessionStream(sessionId) {
    if (!sessionId) return null;
    return this.bySession.get(String(sessionId)) || null;
  }

  stopSessionStream(sessionId) {
    const stream = this.getSessionStream(sessionId);
    if (!stream) return;
    stream.abort();
    this.bySession.delete(String(sessionId));
  }

  async startSessionStream({
    sessionId,
    url,
    payload,
    accessToken,
    startedAtMs = null,
    from = 0,
  }) {
    if (!sessionId || !url) return { usedStream: false };

    const key = String(sessionId);
    const existing = this.bySession.get(key);
    if (existing && ["starting", "streaming"].includes(existing.status)) {
      return { usedStream: true, stream: existing };
    }

    const stream = new ManagedStream({
      sessionId: key,
      url,
      payload,
      accessToken,
      startedAtMs,
    });

    // Open the SSE connection once.
    let resp;
    try {
      const resumeFrom = Number.isFinite(Number(from)) ? Number(from) : 0;
      resp = await stream.makeFetch({ from: Math.max(0, resumeFrom) });
    } catch (err) {
      return { usedStream: false, error: err };
    }

    if (!isEventStreamResponse(resp)) {
      // If backend short-circuits with JSON (e.g. session limit reached),
      // parse it and return a structured hint so callers can lock the UI
      // without making more requests.
      try {
        const contentType = resp?.headers?.get?.("content-type") || "";
        if (contentType.includes("application/json")) {
          const data = await resp.json().catch(() => null);
          const tokenUsage = extractTokenUsage(data);
          const blocked =
            isSessionLimitError(data) ||
            (tokenUsage && isSessionLimitReached(tokenUsage));
          if (blocked) {
            return {
              usedStream: false,
              sessionBlocked: true,
              message: getSessionLimitMessage(data),
              token_usage: tokenUsage || data?.token_usage || null,
              data,
              resp,
            };
          }
          return { usedStream: false, data, resp };
        }
      } catch {
        // ignore parse errors
      }

      return { usedStream: false, resp };
    }

    this.bySession.set(key, stream);
    setTimeout(() => {
      stream.run(resp).finally(() => {
        if (
          this.bySession.get(key) === stream &&
          ["done", "error", "stopped", "aborted"].includes(stream.status)
        ) {
          this.bySession.delete(key);
        }
      });
    }, 0);

    return { usedStream: true, stream };
  }
}

export const chatStreamManager = new ChatStreamManager();
